import type {Dimensions, SniffedImageFormat} from '../types';
import {inspectJpegHeader} from './inspect-jpeg-header';

type HeaderReader = (bytes: Uint8Array) => Dimensions | null;

const view = (bytes: Uint8Array, offset: number, length: number): DataView | null =>
  offset + length <= bytes.length
    ? new DataView(bytes.buffer, bytes.byteOffset + offset, length)
    : null;

const readJpeg: HeaderReader = bytes => inspectJpegHeader(bytes).frame;

const readPng: HeaderReader = bytes => {
  const ihdr = view(bytes, 16, 8);
  return ihdr === null ? null : {width: ihdr.getUint32(0), height: ihdr.getUint32(4)};
};

const readGif: HeaderReader = bytes => {
  const screen = view(bytes, 6, 4);
  return screen === null
    ? null
    : {width: screen.getUint16(0, true), height: screen.getUint16(2, true)};
};

const readBmp: HeaderReader = bytes => {
  const info = view(bytes, 18, 8);
  return info === null
    ? null
    : {width: Math.abs(info.getInt32(0, true)), height: Math.abs(info.getInt32(4, true))};
};

const readWebpLossy = (bytes: Uint8Array): Dimensions | null => {
  const frame = view(bytes, 26, 4);
  return frame === null
    ? null
    : {width: frame.getUint16(0, true) & 0x3fff, height: frame.getUint16(2, true) & 0x3fff};
};

const readWebpLossless = (bytes: Uint8Array): Dimensions | null => {
  if (bytes[20] !== 0x2f) {
    return null;
  }
  const b0 = bytes[21];
  const b1 = bytes[22];
  const b2 = bytes[23];
  const b3 = bytes[24];
  if (b0 === undefined || b1 === undefined || b2 === undefined || b3 === undefined) {
    return null;
  }
  return {
    width: 1 + (b0 | ((b1 & 0x3f) << 8)),
    height: 1 + ((b1 >> 6) | (b2 << 2) | ((b3 & 0x0f) << 10)),
  };
};

const readWebpExtended = (bytes: Uint8Array): Dimensions | null => {
  const canvas = view(bytes, 24, 6);
  if (canvas === null) {
    return null;
  }
  const width = 1 + (canvas.getUint8(0) | (canvas.getUint8(1) << 8) | (canvas.getUint8(2) << 16));
  const height = 1 + (canvas.getUint8(3) | (canvas.getUint8(4) << 8) | (canvas.getUint8(5) << 16));
  return {width, height};
};

const WEBP_READER_BY_CHUNK: Readonly<Record<string, HeaderReader>> = {
  'VP8 ': readWebpLossy,
  VP8L: readWebpLossless,
  VP8X: readWebpExtended,
};

const readWebp: HeaderReader = bytes => {
  const chunk = String.fromCharCode(bytes[12] ?? 0, bytes[13] ?? 0, bytes[14] ?? 0, bytes[15] ?? 0);
  return WEBP_READER_BY_CHUNK[chunk]?.(bytes) ?? null;
};

const unknown: HeaderReader = () => null;

const HEADER_READER_BY_FORMAT: Readonly<Record<SniffedImageFormat, HeaderReader>> = {
  jpeg: readJpeg,
  png: readPng,
  webp: readWebp,
  gif: readGif,
  bmp: readBmp,
  heic: unknown,
  avif: unknown,
};

/** Stored (un-oriented) pixel dimensions from the container header, without decoding. */
export const readImageHeader = (format: SniffedImageFormat, bytes: Uint8Array): Dimensions | null =>
  HEADER_READER_BY_FORMAT[format](bytes);
